import React, { useEffect } from "react";
import { Loading, useForm, useToast } from "@venuepos/react-common";
import {
    GQFiskalySettings,
    useCashRegistersQuery,
    useFiskalyClientsQuery,
    useFiskalyEstablishmentMappingLazyQuery,
    useFiskalyEstablishmentsQuery,
    useSaveFiskalyEstablishmentMappingMutation,
} from "graphql-sdk";
import { useCallback } from "react";
import { FiskalyEstablishmentMappingForm } from "./mapping-form";
import { useTranslation } from "locales";
import { NIL } from "uuid";
import { useHandleMutationError } from "../../../../hooks";
import { FiskalyEstablishmentMappingType } from "../fiskaly-form";

export function FiskalyEstablishmentMapping({
    settings,
}: {
    settings: GQFiskalySettings;
}) {
    const [t] = useTranslation();
    const toast = useToast();
    const cashRegisters = useCashRegistersQuery({
        variables: {
            pagination: {
                page: 0,
                pageSize: 9999,
                sort: "name",
                sortDirection: "ASC",
            },
        },
        fetchPolicy: "no-cache",
    });

    const clients = useFiskalyClientsQuery({
        fetchPolicy: "no-cache",
    });
    const [getEstablishmentMapping, establishmentMappingResponse] =
        useFiskalyEstablishmentMappingLazyQuery();

    const establishments = useFiskalyEstablishmentsQuery({
        variables: {
            integrationId: settings.integrationId,
        },
        fetchPolicy: "no-cache",
    });
    const [saveMapping] = useSaveFiskalyEstablishmentMappingMutation();
    const { handleMutationError } = useHandleMutationError();
    const form = useForm<FiskalyEstablishmentMappingType>(
        { establishmentMap: { required: true } },
        null
    );
    const [{ values }, { setDefaultValues }] = form;

    const save = useCallback(async () => {
        if (!settings) {
            return;
        }

        if (!values) {
            return;
        }

        await handleMutationError(
            async () => {
                await saveMapping({
                    variables: {
                        integrationId: settings.integrationId,
                        establishmentMapping: values.establishmentMap,
                    },
                });
            },
            t("common.saved", "Saved"),
            () => {
                toast.info(
                    t(
                        "backoffice.integration.fiskaly.establishment_mapping_reminder",
                        "Reminder: Changes should be submitted to German authorities soon."
                    )
                );
                clients.refetch();
            }
        );
    }, [clients, handleMutationError, saveMapping, settings, t, toast, values]);

    // Get the mapping values
    useEffect(() => {
        if (!settings?.organizationId || settings.organizationId === NIL) {
            return;
        }

        if (establishmentMappingResponse.called) {
            return;
        }

        getEstablishmentMapping({
            variables: {
                integrationId: settings.integrationId,
            },
            fetchPolicy: "no-cache",
        });
    }, [
        establishmentMappingResponse.called,
        getEstablishmentMapping,
        settings.integrationId,
        settings.organizationId,
    ]);

    // Set the mapping values when they are loaded.
    useEffect(() => {
        if (!establishmentMappingResponse?.data?.fiskalyEstablishmentMapping) {
            return;
        }

        setDefaultValues({
            establishmentMap:
                establishmentMappingResponse?.data?.fiskalyEstablishmentMapping.data.map(
                    itm => ({
                        clientId: itm.clientId,
                        cashRegisterId: itm.cashRegisterId,
                        establishmentId: itm.establishmentId,
                    })
                ),
        });
    }, [establishmentMappingResponse, setDefaultValues]);

    if (establishmentMappingResponse.loading || cashRegisters.loading) {
        return <Loading />;
    }

    return (
        <FiskalyEstablishmentMappingForm
            form={form}
            onSubmit={save}
            establishments={establishments?.data?.fiskalyEstablishments}
            cashRegisters={cashRegisters?.data?.cashRegisters}
            clients={clients?.data?.fiskalyClients}
        />
    );
}
